import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke } from '../lib/safeInvoke';
import { safeParseJSON } from '../lib/sanitizeDisplay';
import { PhaseLoader } from '../components/shared/PhaseLoader';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';

interface HumanDesignResult {
  type: string;
  profile: string;
  authority: string;
  strategy: string;
  notSelf: string;
  signature: string;
  centers: { name: string; defined: boolean; description: string }[];
  sections: {
    tipo?: string;
    estrategia?: string;
    autoridade?: string;
    centros?: string;
    sinteseGeral?: string;
  };
  readingId: string | null;
  text: string;
}

const TABS = [
  { key: 'sinteseGeral', label: '✨ Síntese' },
  { key: 'tipo',         label: '🌟 Tipo' },
  { key: 'estrategia',   label: '🎯 Estratégia' },
  { key: 'autoridade',   label: '💜 Autoridade' },
  { key: 'centros',      label: '⚡ Centros' },
];

function TypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    'Manifestador':  '#f87171',
    'Gerador':       '#4ade80',
    'Gerador Manifestador': '#a78bfa',
    'Projetor':      '#60a5fa',
    'Refletor':      '#fbbf24',
  };
  const color = Object.entries(colors).find(([k]) => type.includes(k))?.[1] ?? 'var(--petrol)';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', backgroundColor: 'var(--bg-elevated)', border: `2px solid ${color}`, borderRadius: 'var(--radius-full)' }}>
      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{type}</span>
    </div>
  );
}

const LOADING_PHASES = [
  'Calculando seu tipo de Design Humano...',
  'Analisando centros de energia definidos e abertos...',
  'Identificando sua estratégia e autoridade interna...',
  'Preparando seu mapa de Design Humano...',
];

export function HumanDesignPage() {
  useSystemUsageTracker('design_humano');
  const { profile } = useAuth();
  const navigate    = useNavigate();
  const { t, lang } = useI18n();

  const [loading,   setLoading]   = useState(false);
  const [result,    setResult]    = useState<HumanDesignResult | null>(null);
  const [error,     setError]     = useState<string | null>(null);
  const [started,   setStarted]   = useState(false);

  useEffect(() => {
    if (started) { setResult(null); setStarted(false); }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  async function generate() {
    setStarted(true);
    setLoading(true);
    setError(null);

    const { data, fromFallback } = await safeInvoke<HumanDesignResult>('human-design', {
      body:     {},
      fallback: { type: '', profile: '', authority: '', strategy: '', notSelf: '', signature: '', centers: [], sections: {}, readingId: null, text: '' },
      timeout:  30_000,
    });

    setLoading(false);
    if (fromFallback || !data.text) {
      setError(t('pages.humanDesign.error'));
      return;
    }
    // Client-side JSON section parsing fallback
    let sections = data.sections;
    let text = data.text;
    if (!sections?.sinteseGeral) {
      const parsed = safeParseJSON<{ sinteseGeral?: string; tipo?: string; estrategia?: string; autoridade?: string; centros?: string }>(text);
      if (parsed?.sinteseGeral) {
        sections = { sinteseGeral: parsed.sinteseGeral, tipo: parsed.tipo ?? '', estrategia: parsed.estrategia ?? '', autoridade: parsed.autoridade ?? '', centros: parsed.centros ?? '' };
        text = parsed.sinteseGeral;
      } else if (text.trimStart().startsWith('{')) {
        text = 'Design Humano temporariamente indisponível ✦';
      }
    }
    setResult({ ...data, sections, text });
  }

  if (!profile?.birth_date) {
    return (
      <div className="page-container">
        <EmptyState emoji="⚡" title={t('pages.humanDesign.emptyTitle')}
          subtitle={t('pages.humanDesign.emptySubtitle')}
          cta={t('pages.humanDesign.emptyCta')} ctaAction={() => navigate('/profile')} />
      </div>
    );
  }

  const needsBirthTime = !profile?.birth_time;

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          {t('pages.humanDesign.title')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          {t('pages.humanDesign.subtitle')}
        </p>
      </div>

      {needsBirthTime && (
        <div className="card" style={{ marginBottom: '1.5rem', border: '1px solid var(--border-gold)', backgroundColor: 'rgba(31,78,95,0.06)' }}>
          <p style={{ color: 'var(--petrol)', fontWeight: 600, fontSize: 'var(--text-label)', marginBottom: '0.5rem' }}>
            {t('pages.humanDesign.birthTimeWarning')}
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', marginBottom: '1rem', lineHeight: 1.5 }}>
            {t('pages.humanDesign.birthTimeWarningDesc')}
          </p>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/profile')}>
            {t('pages.humanDesign.addBirthTime')}
          </button>
        </div>
      )}

      {!started && !result && (
        <div className="card" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem' }}>⚡</span>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            {t('pages.humanDesign.intro')}
          </p>
          <button className="btn btn-primary" onClick={generate}>
            {t('pages.humanDesign.calculateCta')}
          </button>
        </div>
      )}

      {loading && (
        <div className="card">
          <PhaseLoader phases={LOADING_PHASES} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '1rem', backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 'var(--radius-md)', color: '#fca5a5', fontSize: 'var(--text-label)', marginBottom: '1.5rem' }}>
          {error}
          <br />
          <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={generate}>
            Tentar novamente
          </button>
        </div>
      )}

      {result && !loading && (
        <div className="animate-fade-in">
          {/* Type + quick stats */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem' }}>
              <TypeBadge type={result.type} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { label: t('pages.humanDesign.profileLabel'), value: result.profile },
                { label: t('pages.humanDesign.authorityLabel'), value: result.authority },
                { label: t('pages.humanDesign.strategyLabel'), value: result.strategy },
                { label: t('pages.humanDesign.signatureLabel'), value: result.signature },
              ].map(item => (
                <div key={item.label} style={{ backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 0.25rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{item.label}</p>
                  <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 600, margin: 0 }}>{item.value || '-'}</p>
                </div>
              ))}
            </div>

            {result.notSelf && (
              <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'rgba(239,68,68,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(239,68,68,0.2)' }}>
                <p style={{ fontSize: 'var(--text-caption)', color: '#fca5a5', margin: '0 0 0.25rem', fontWeight: 600 }}>{t('pages.humanDesign.notSelfLabel')}</p>
                <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', margin: 0 }}>{result.notSelf}</p>
              </div>
            )}
          </div>

          {/* Centers */}
          {(result.centers?.length ?? 0) > 0 && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                {t('pages.humanDesign.centersTitle')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {result.centers.map((c, i) => (
                  <div key={i} style={{ padding: '0.35rem 0.75rem', backgroundColor: c.defined ? 'rgba(31,78,95,0.15)' : 'var(--bg-base)', border: `1px solid ${c.defined ? 'var(--border-gold)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-full)', fontSize: 'var(--text-caption)', color: c.defined ? 'var(--petrol)' : 'var(--text-muted)' }}>
                    {c.defined ? '●' : '○'} {c.name}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stacked sections */}
          {TABS.map(tab => {
            const sections = result.sections as Record<string, string> | undefined ?? {};
            const text = sections[tab.key] || (tab.key === 'sinteseGeral' ? result.text : '');
            if (!text) return null;
            return (
              <div key={tab.key} className="card" style={{ marginBottom: '1.5rem' }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
                  {tab.label}
                </p>
                <div className="reading-text">
                  {text.split('\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            );
          })}

          <ReadingActions pageName="Design Humano" reading={result.text} signEmoji="⚡" />
          <ReadingFeedback feature="human-design" readingId={result.readingId ?? undefined} />

          <button className="btn btn-secondary btn-sm" style={{ marginTop: '1rem' }} onClick={() => { setResult(null); setStarted(false); }}>
            {t('pages.humanDesign.recalculate')}
          </button>
        </div>
      )}
    </div>
  );
}
